import React, { useState } from 'react';
import { collection, getDocs, query, where, doc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';

const CSVUpload = ({ onUploadComplete }) => {
  const [uploading, setUploading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [, setPreviewData] = useState([]);
  const [validationResults, setValidationResults] = useState({});
  const [existingEmails, setExistingEmails] = useState(new Set());

  // Function to parse CSV content
  const parseCSV = (csvContent) => {
    const lines = csvContent.split('\n').map(line => line.trim()).filter(line => line);
    if (lines.length < 2) return [];

    const headers = lines[0].split(',').map(header => header.replace(/"/g, '').trim());
    const data = [];

    for (let i = 1; i < lines.length; i++) {
      const values = [];
      let current = '';
      let inQuotes = false;
      
      for (let j = 0; j < lines[i].length; j++) {
        const char = lines[i][j];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          values.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current.trim());

      if (values.length === headers.length) {
        const rowData = {};
        headers.forEach((header, index) => {
          let value = values[index].replace(/^"|"$/g, '').replace(/""/g, '"');
          
          // Convert numeric strings to numbers
          if (!isNaN(value) && !isNaN(parseFloat(value)) && value !== '') {
            value = parseFloat(value);
          }
          
          // Convert boolean strings
          if (value === 'true') value = true;
          if (value === 'false') value = false;
          
          // Convert date strings to Date objects for date fields
          if (['createdAt', 'dob', 'lastUpdated', 'emailMigratedAt'].includes(header) && value) {
            try {
              const date = new Date(value);
              if (!isNaN(date.getTime())) {
                value = date;
              }
            } catch (e) {
              // Keep original value if date parsing fails
            }
          }
          
          rowData[header] = value;
        });
        data.push(rowData);
      }
    }

    return data;
  };

  // Function to validate employee data
  const validateEmployeeData = (employees) => {
    const results = {
      valid: [],
      invalid: [],
      existing: [],
      errors: {}
    };

    employees.forEach((employee, index) => {
      const errors = [];
      
      // Required fields validation
      if (!employee.name || !employee.name.trim()) errors.push('Name is required');
      if (!employee.email || !employee.email.trim()) errors.push('Email is required');
      if (!employee.empID || !employee.empID.trim()) errors.push('Employee ID is required');
      
      // Email format validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (employee.email && !emailRegex.test(employee.email)) {
        errors.push('Invalid email format');
      }
      
      // Check if email already exists in current data
      const emailInCurrentData = employees.filter((emp, i) => i !== index && emp.email === employee.email);
      if (emailInCurrentData.length > 0) {
        errors.push('Duplicate email in uploaded data');
      }
      
      // Check if email exists in database
      if (existingEmails.has(employee.email)) {
        results.existing.push({ ...employee, rowIndex: index + 2 }); // +2 for header and 0-based index
      } else if (errors.length > 0) {
        results.invalid.push({ ...employee, rowIndex: index + 2 });
        results.errors[index] = errors;
      } else {
        results.valid.push({ ...employee, rowIndex: index + 2 });
      }
    });

    return results;
  };

  // Function to check existing emails in database
  const checkExistingEmails = async (emails) => {
    const existing = new Set();
    const myelinersRef = collection(db, 'myeliners');
    
    // Check emails in batches to avoid Firestore limitations
    const batchSize = 10;
    for (let i = 0; i < emails.length; i += batchSize) {
      const batch = emails.slice(i, i + batchSize);
      const queries = batch.map(email => 
        getDocs(query(myelinersRef, where('email', '==', email)))
      );
      
      const results = await Promise.all(queries);
      results.forEach((snapshot, index) => {
        if (!snapshot.empty) {
          existing.add(batch[index]);
        }
      });
    }
    
    return existing;
  };

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      alert('Please upload a CSV file');
      return;
    }

    try {
      setUploading(true);
      
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const csvContent = e.target.result;
          const parsedData = parseCSV(csvContent);
          
          if (parsedData.length === 0) {
            alert('No valid data found in CSV file');
            return;
          }

          // Check existing emails
          const emails = parsedData.map(row => row.email).filter(email => email);
          const existing = await checkExistingEmails(emails);
          setExistingEmails(existing);
          
          // Validate data
          const validation = validateEmployeeData(parsedData);
          
          setCsvData(parsedData);
          setPreviewData(validation);
          setValidationResults(validation);
          setShowPreview(true);
          
        } catch (error) {
          console.error('Error parsing CSV:', error);
          alert('Error parsing CSV file. Please check the format.');
        } finally {
          setUploading(false);
        }
      };
      
      reader.readAsText(file);
    } catch (error) {
      console.error('Error reading file:', error);
      alert('Error reading file');
      setUploading(false);
    }
  };

  // Handle bulk upload confirmation
  const handleConfirmUpload = async () => {
    try {
      setUploading(true);
      
      const { valid } = validationResults;
      if (valid.length === 0) {
        alert('No valid records to upload');
        return;
      }

      let successCount = 0;
      const errors = [];

      for (const employee of valid) {
        try {
          // Prepare employee data with required defaults
          const employeeData = {
            name: employee.name || '',
            email: employee.email || '',
            personalEmail: employee.personalEmail || '',
            empID: employee.empID || '',
            designation: employee.designation || '',
            organogram: employee.organogram || 'technical',
            dob: employee.dob || null,
            phone: employee.phone || '',
            whatsApp: employee.whatsApp || '',
            address: employee.address || '',
            country: employee.country || '',
            location: employee.location || '',
            emergencyContact: employee.emergencyContact || '',
            emergencyNumber: employee.emergencyNumber || '',
            emergencyRelation: employee.emergencyRelation || '',
            preferredLang: employee.preferredLang || 'English',
            allLang: employee.allLang || '',
            userType: employee.userType || 'user',
            leaveBalance: employee.leaveBalance || 21,
            leaveTaken: employee.leaveTaken || 0,
            myelinLive: employee.myelinLive !== undefined ? employee.myelinLive : true,
            status: employee.status || 'active',
            createdAt: employee.createdAt || new Date(),
            lastUpdated: new Date(),
            // Add any other fields from the CSV
            ...Object.keys(employee).reduce((acc, key) => {
              if (!['name', 'email', 'personalEmail', 'empID', 'designation', 'organogram', 
                    'dob', 'phone', 'whatsApp', 'address', 'country', 'location',
                    'emergencyContact', 'emergencyNumber', 'emergencyRelation', 
                    'preferredLang', 'allLang', 'userType', 'leaveBalance', 'leaveTaken',
                    'myelinLive', 'status', 'createdAt', 'lastUpdated', 'rowIndex'].includes(key)) {
                acc[key] = employee[key];
              }
              return acc;
            }, {})
          };

          // Use email as document ID
          const employeeRef = doc(db, 'myeliners', employee.email);
          await setDoc(employeeRef, employeeData);
          successCount++;
          
        } catch (error) {
          console.error(`Error adding employee ${employee.email}:`, error);
          errors.push(`Failed to add ${employee.email}: ${error.message}`);
        }
      }

      if (errors.length > 0) {
        alert(`Upload completed with errors:\n${successCount} successful\n${errors.length} failed\n\nErrors:\n${errors.join('\n')}`);
      } else {
        alert(`Successfully uploaded ${successCount} employees`);
      }

      setShowPreview(false);
      setCsvData([]);
      setPreviewData([]);
      setValidationResults({});
      setExistingEmails(new Set());
      
      // Reset file input
      const fileInput = document.getElementById('csvUpload');
      if (fileInput) fileInput.value = '';
      
      // Notify parent to refresh employee list
      if (onUploadComplete) onUploadComplete();
      
    } catch (error) {
      console.error('Error during bulk upload:', error);
      alert('Error during bulk upload');
    } finally {
      setUploading(false);
    }
  };

  const closePreview = () => {
    setShowPreview(false);
    setCsvData([]);
    setPreviewData([]);
    setValidationResults({});
    setExistingEmails(new Set());
    
    // Reset file input
    const fileInput = document.getElementById('csvUpload');
    if (fileInput) fileInput.value = '';
  };

  return (
    <>
      <div className="csv-upload-container">
        <label htmlFor="csvUpload" className="csv-upload-btn">
          <input
            type="file"
            id="csvUpload"
            accept=".csv"
            onChange={handleFileUpload}
            disabled={uploading}
            style={{ display: 'none' }}
          />
          {uploading ? 'Processing...' : 'Import Employees from CSV'}
        </label>
      </div>

      {showPreview && (
        <div className="csv-preview-modal">
          <div className="csv-preview-content">
            <div className="preview-header">
              <h3>CSV Import Preview</h3>
              <button className="close-btn" onClick={closePreview}>×</button>
            </div>
            
            <div className="preview-summary">
              <div className="summary-stats">
                <span className="stat valid">Valid: {validationResults.valid?.length || 0}</span>
                <span className="stat invalid">Invalid: {validationResults.invalid?.length || 0}</span>
                <span className="stat existing">Existing: {validationResults.existing?.length || 0}</span>
              </div>
            </div>

            <div className="preview-sections">
              {validationResults.valid?.length > 0 && (
                <div className="section valid-section">
                  <h4>Valid Records ({validationResults.valid.length})</h4>
                  <div className="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>Row</th>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Employee ID</th>
                          <th>Designation</th>
                          <th>Department</th>
                        </tr>
                      </thead>
                      <tbody>
                        {validationResults.valid.map((employee, index) => (
                          <tr key={index}>
                            <td>{employee.rowIndex}</td>
                            <td>{employee.name}</td>
                            <td>{employee.email}</td>
                            <td>{employee.empID}</td>
                            <td>{employee.designation}</td>
                            <td>{employee.organogram}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {validationResults.invalid?.length > 0 && (
                <div className="section invalid-section">
                  <h4>Invalid Records ({validationResults.invalid.length})</h4>
                  <div className="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>Row</th>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Employee ID</th>
                          <th>Errors</th>
                        </tr>
                      </thead>
                      <tbody>
                        {validationResults.invalid.map((employee, index) => (
                          <tr key={index}>
                            <td>{employee.rowIndex}</td>
                            <td>{employee.name}</td>
                            <td>{employee.email}</td>
                            <td>{employee.empID}</td>
                            <td className="error-cell">
                              {validationResults.errors[csvData.findIndex(emp => emp.email === employee.email)]?.join(', ')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {validationResults.existing?.length > 0 && (
                <div className="section existing-section">
                  <h4>Existing Records (Will be skipped) ({validationResults.existing.length})</h4>
                  <div className="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>Row</th>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Employee ID</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {validationResults.existing.map((employee, index) => (
                          <tr key={index}>
                            <td>{employee.rowIndex}</td>
                            <td>{employee.name}</td>
                            <td>{employee.email}</td>
                            <td>{employee.empID}</td>
                            <td>Already exists in database</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="preview-actions">
              <button className="cancel-btn" onClick={closePreview} disabled={uploading}>
                Cancel
              </button>
              <button 
                className="confirm-btn" 
                onClick={handleConfirmUpload} 
                disabled={uploading || (validationResults.valid?.length || 0) === 0}
              >
                {uploading ? 'Uploading...' : `Upload ${validationResults.valid?.length || 0} Valid Records`}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CSVUpload; 